import React, { useEffect } from 'react';
import { User, BookOpen, Clock, X } from 'lucide-react';
import FormInput from '../FormInput';
import useEnseignantData from '../../hooks/useEnseignantData';

const AssiduiteForm = ({
  showModal,
  setShowModal,
  activeTab,
  formData,
  setFormData,
  selectedClasse,
  setSelectedClasse,
  selectedModule,
  setSelectedModule,
  classes,
  etudiants,
  enseignants,
  modules,
  handleSubmit,
  resetForm,
  isEditing = false
}) => {
  // Hook pour gérer les données spécifiques aux enseignants
  const {
    classesEnseignant,
    modulesEnseignant,
    loadingClasses,
    loadingModules,
    fetchClassesByEnseignant,
    fetchModulesByEnseignantClasse,
    resetEnseignantData
  } = useEnseignantData();

  // Effet pour charger les classes quand un enseignant est sélectionné
  useEffect(() => {
    if (activeTab === 'enseignants' && formData.enseignant) {
      fetchClassesByEnseignant(formData.enseignant);
    } else if (activeTab === 'enseignants' && !formData.enseignant) {
      resetEnseignantData();
      setSelectedClasse('');
      setSelectedModule('');
    }
  }, [activeTab, formData.enseignant, fetchClassesByEnseignant, resetEnseignantData, setSelectedClasse, setSelectedModule]);

  // Effet pour charger les modules quand une classe est sélectionnée pour un enseignant
  useEffect(() => {
    if (activeTab === 'enseignants' && formData.enseignant && selectedClasse) {
      fetchModulesByEnseignantClasse(formData.enseignant, selectedClasse);
    } else if (activeTab === 'enseignants') {
      setSelectedModule('');
    }
  }, [activeTab, formData.enseignant, selectedClasse, fetchModulesByEnseignantClasse, setSelectedModule]);

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto transform transition-all shadow-2xl animate-in zoom-in-95 duration-200">
        {/* Header optimisé */}
        <div className="sticky top-0 bg-gradient-to-r from-[#202654] via-[#2a2f6c] to-[#202654] px-6 py-4 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                {activeTab === 'etudiants' ? (
                  <User className="h-5 w-5 text-white" />
                ) : (
                  <BookOpen className="h-5 w-5 text-white" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">
                  {isEditing ? 'Modifier' : 'Nouveau'} Retard
                </h3>
                <p className="text-blue-100 text-sm">
                  {activeTab === 'etudiants' ? 'Retard étudiant' : 'Retard enseignant'}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
              className="p-2 text-white hover:text-gray-200 transition-colors rounded-full hover:bg-white/10"
              aria-label="Fermer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Formulaire compact */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Section 1: Informations de base */}
          <div className="space-y-4">
            <div className="border-l-4 border-[#202654] pl-3">
              <h4 className="text-base font-semibold text-gray-900">Informations de base</h4>
              <p className="text-sm text-gray-600">Sélectionnez la personne concernée</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Pour les étudiants: Classe en premier */}
              {activeTab === 'etudiants' && (
                <div className="md:col-span-2">
                  <FormInput
                    label="Classe"
                    type="select"
                    name="classe"
                    value={selectedClasse}
                    onChange={(e) => {
                      setSelectedClasse(e.target.value);
                      setSelectedModule(''); // Reset module quand on change de classe
                    }}
                    options={[
                      { value: '', label: 'Sélectionner une classe' },
                      ...classes.map((classe) => ({
                        value: classe.id,
                        label: classe.libelle
                      }))
                    ]}
                    required
                    icon={BookOpen}
                    size="compact"
                  />
                </div>
              )}

              {/* Personne - Étudiant en deuxième pour les étudiants */}
              <div className="md:col-span-2">
                <FormInput
                  label={activeTab === 'etudiants' ? 'Étudiant' : 'Enseignant'}
                  type="select"
                  name={activeTab === 'etudiants' ? 'etudiant' : 'enseignant'}
                  value={activeTab === 'etudiants' ? formData.etudiant : formData.enseignant}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      [activeTab === 'etudiants' ? 'etudiant' : 'enseignant']: e.target.value
                    });
                    // Pour les enseignants, reset classe et module quand on change de personne
                    if (activeTab === 'enseignants') {
                      setSelectedClasse('');
                      setSelectedModule('');
                    }
                  }}
                  options={[
                    { 
                      value: '', 
                      label: `Choisir ${activeTab === 'etudiants' ? 'un étudiant' : 'un enseignant'}` 
                    },
                    ...(activeTab === 'etudiants' ? etudiants : enseignants).map((person) => ({
                      value: person.id,
                      label: `${person.nom} ${person.prenom}${person.matricule ? ` (${person.matricule})` : ''}`
                    }))
                  ]}
                  required
                  icon={User}
                  size="compact"
                />
              </div>

              {/* Classe - Pour les enseignants seulement */}
              {activeTab === 'enseignants' && (
                <div className="md:col-span-2">
                  <FormInput
                    label="Classe"
                    type="select"
                    name="classe"
                    value={selectedClasse}
                    onChange={(e) => {
                      setSelectedClasse(e.target.value);
                      setSelectedModule(''); // Reset module quand on change de classe
                    }}
                    options={[
                      { value: '', label: 'Sélectionner une classe' },
                      ...classesEnseignant.map((classe) => ({
                        value: classe.id,
                        label: classe.libelle
                      }))
                    ]}
                    disabled={!formData.enseignant || loadingClasses}
                    icon={BookOpen}
                    size="compact"
                    helperText={
                      !formData.enseignant 
                        ? "Sélectionnez d'abord un enseignant"
                        : loadingClasses
                        ? "Chargement des classes..."
                        : undefined
                    }
                  />
                </div>
              )}
            </div>
          </div>

          {/* Section 2: Détails du retard */}
          <div className="space-y-4">
            <div className="border-l-4 border-orange-500 pl-3">
              <h4 className="text-base font-semibold text-gray-900">Détails du retard</h4>
              <p className="text-sm text-gray-600">Renseignez les informations du retard</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Module */}
              <div className="md:col-span-2">
                <FormInput
                  label="Module (optionnel)"
                  type="select"
                  name="module"
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                  options={[
                    { value: '', label: 'Aucun module spécifique' },
                    ...(activeTab === 'etudiants' 
                      ? modules.map((module) => ({
                          value: module.id,
                          label: module.libelle
                        }))
                      : modulesEnseignant.map((module) => ({
                          value: module.id,
                          label: module.libelle
                        }))
                    )
                  ]}
                  disabled={
                    activeTab === 'enseignants' && 
                    (!formData.enseignant || !selectedClasse || loadingModules)
                  }
                  icon={BookOpen}
                  size="compact"
                  helperText={
                    activeTab === 'enseignants' && !formData.enseignant
                      ? "Sélectionnez d'abord un enseignant"
                      : activeTab === 'enseignants' && !selectedClasse
                      ? "Sélectionnez une classe"
                      : activeTab === 'enseignants' && loadingModules
                      ? "Chargement des modules..."
                      : undefined
                  }
                />
              </div>

              {/* Temps et Motif sur la même ligne */}
              <div>
                <FormInput
                  label="Temps (minutes)"
                  type="number"
                  name="temps"
                  value={formData.temps}
                  onChange={(e) => setFormData({ ...formData, temps: e.target.value })}
                  required
                  min="1"
                  max="180"
                  placeholder="Ex: 15"
                  icon={Clock}
                  size="compact"
                />
              </div>

              <div>
                <FormInput
                  label="Motif"
                  type="text"
                  name="motif"
                  value={formData.motif}
                  onChange={(e) => setFormData({ ...formData, motif: e.target.value })}
                  placeholder="Raison du retard"
                  size="compact"
                />
              </div>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
              className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-[#202654] to-[#2a2f6c] hover:from-[#1a1f4a] hover:to-[#242862] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              {isEditing ? 'Modifier' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssiduiteForm;
